import { useEffect, useMemo, useState } from "react";

import {
  asRetained,
  isGenerationError,
  isSectionUnavailable,
  SectionResponse,
} from "../../../../services";
import { useSectionRetry } from "./useSectionRetry";
import { UseRetainedSectionProps, UseRetainedSectionReturn } from "./types";

interface LastGood<Result> {
  scopeKey: string;
  response: SectionResponse<Result>;
}

// A presentation cache, not a server snapshot: it keeps the last good payload on screen while one section retries.
export const useRetainedSection = <Result>({
  query,
  section,
  scopeKey,
  generation,
}: UseRetainedSectionProps<Result>): UseRetainedSectionReturn<Result> => {
  const { currentData, error, refetch, isFetching = false } = query;
  const {
    generation: queryGeneration,
    handleSectionError,
    reportDisplayed,
  } = generation;
  const [lastGood, setLastGood] = useState<LastGood<Result> | null>(null);
  const isNewPayload =
    currentData !== undefined &&
    (lastGood?.response !== currentData || lastGood.scopeKey !== scopeKey);
  if (isNewPayload) {
    setLastGood({ scopeKey, response: currentData });
  }
  const heldForScope =
    lastGood?.scopeKey === scopeKey ? lastGood.response : undefined;
  const retainable = isNewPayload ? currentData : heldForScope;

  const isSectionFailure =
    currentData === undefined && isSectionUnavailable(error);
  const retained = useMemo(
    () => (isSectionFailure && retainable ? asRetained(retainable) : undefined),
    [isSectionFailure, retainable],
  );

  // Any earlier payload for this scope stays up: a pending generation, a restart and a failed refresh alike.
  const response = currentData ?? retained ?? retainable;
  const isSectionError = error !== undefined && !isGenerationError(error);
  const visibleError =
    response === undefined && isSectionError ? error : undefined;

  useEffect(() => {
    if (error !== undefined) {
      handleSectionError(error, queryGeneration);
    }
  }, [error, handleSectionError, queryGeneration]);

  useSectionRetry(error, refetch);

  const shownVersion = response?.accountVersion ?? null;
  const shownAccountId = response?.accountId ?? null;
  const isShown = response !== undefined;
  useEffect(() => {
    reportDisplayed(
      section,
      isShown
        ? { accountVersion: shownVersion, accountId: shownAccountId }
        : null,
    );
  }, [reportDisplayed, section, isShown, shownVersion, shownAccountId]);
  useEffect(
    () => () => reportDisplayed(section, null),
    [reportDisplayed, section],
  );

  return {
    response,
    isRetained: response !== undefined && response === retained,
    error: visibleError,
    refreshFailed: response !== undefined && isSectionError,
    isRefreshing: isFetching,
  };
};
